// Copyright (c) Docugami, Inc. All rights reserved.

import { Dictionary } from "@reduxjs/toolkit";
import { TokenRange } from "networking/api";
import { Box } from "store/shared_types";
import { TokenKeyRange } from "utils/MappedTokens";

export interface AnnotationsUpdatedMessage {
    organizationId: string;
    docsetId: string;
    fileId: string;
    annotationEventId: string;
}

export interface Label {
    toolId: string;
    value: string;
    confidence: number;
}

export interface Chunk {
    docsetId: string;
    fileId: string;
    spanId: string;
    tokenKeyRange: TokenKeyRange;
    labels: Label[];
    parent?: string;
    children: string[];
    isRoot?: boolean;
    structure?: ChunkStructureInfo;
    style?: ChunkStyleInfo;
    tokenRange: TokenRange;
    outflowTokenRange?: TokenRange;
    transient?: boolean;
    addedChunkValue?: string;
}

export type ChunkStructureInfo = {
    value: string;
    type: ChunkStructure;
    rowspan?: number;
    colspan?: number;
}

export type ChunkStyleInfo = {
    value: string;
    boundingBoxes?: Box[];
}

export enum ChunkStructure {
    Table = 'table',
    TableBody = 'tbody',
    TableRow = 'tr',
    TableCell = 'td',

    OrderedList = 'ol',
    UnorderedList = 'ul',
    ListItem = 'li',
    ListItemMarker = 'lim',

    Heading1 = 'h1',
    Div = 'div',
    Paragraph = 'p',
    Span = 'span',

    OutflowContainer = 'outflow_container',
    Figure = 'figure',
    FigureCaption = 'figcaption',
    Image = 'img',
    PageHeader = 'header',
    PageFooter = 'footer'
}

export interface SimpleColumn {
    header: Chunk;
    rows: Chunk[];
}

export const isOutflowStructure = (structure?: ChunkStructure) => structure === ChunkStructure.OutflowContainer;
export const isTableStructure = (structure?: ChunkStructure) => structure === ChunkStructure.Table || structure === ChunkStructure.TableBody || structure === ChunkStructure.TableRow || structure === ChunkStructure.TableCell;
export const isSpanIdAddedChunk = (spanId?: string) => !!spanId?.includes(".0.0.");
export const isChunkAddedChunk = (chunk?: Chunk) => !!chunk && chunk.tokenKeyRange.fromTokenKey === "0" && chunk.tokenKeyRange.toTokenKey === "0";

export function chunkPostOrderTraversal(root: Chunk, entities: Dictionary<Chunk>): any {
    function* walker(e: Chunk | undefined, index: number = 0): any {
        if (!e) {
            return;
        }

        yield* walker(entities[e.children[0] ?? ''] ?? undefined, 0);

        yield e;

        if (e === root) {
            return;
        }

        const parent = entities[e.parent ?? ''];
        yield* walker(entities[parent?.children[index + 1] ?? ''] ?? undefined, index + 1);
    }

    return walker(root, 0);
}
